import React, { useContext, useRef } from 'react';
import {
  AppBar, Toolbar, Typography, IconButton, Button, Box, Chip,
  useTheme, alpha, Tooltip, Divider, useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  Menu as MenuIcon,
  DarkMode as DarkModeIcon,
  LightMode as LightModeIcon,
  Park as TreeIcon,
  Dashboard as DashboardIcon,
  AccountTree as AccountTreeIcon,
  Timeline as TimelineIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  SaveAlt as SaveAltIcon,
  Compare as CompareIcon,
} from '@mui/icons-material';
import ColorModeContext from '../ColorModeContext';

type View = 'tree' | 'dashboard' | 'timeline';

interface Props {
  onAddMember: () => void;
  onToggleSidebar: () => void;
  memberCount: number;
  view: View;
  onSetView: (v: View) => void;
  onExportGEDCOM: () => void;
  onImportGEDCOM: (file: File) => void;
  onOpenExport: () => void;
  onOpenEngineCompare: () => void;
}

const VIEW_BUTTONS: { value: View; icon: React.ReactNode; label: string; labelShort: string }[] = [
  { value: 'tree',      icon: <AccountTreeIcon sx={{ fontSize: 16 }} />, label: 'Arbre',     labelShort: 'Arbre' },
  { value: 'timeline',  icon: <TimelineIcon    sx={{ fontSize: 16 }} />, label: 'Chronologie', labelShort: 'Chrono' },
  { value: 'dashboard', icon: <DashboardIcon   sx={{ fontSize: 16 }} />, label: 'Statistiques', labelShort: 'Stats' },
];

const AppHeader: React.FC<Props> = ({
  onAddMember, onToggleSidebar, memberCount, view, onSetView, onExportGEDCOM, onImportGEDCOM, onOpenExport,
  onOpenEngineCompare,
}) => {
  const theme = useTheme();
  const { toggleColorMode } = useContext(ColorModeContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onImportGEDCOM(file);
      e.target.value = '';
    }
  };

  return (
    <AppBar position="static" elevation={0} sx={{
      backgroundColor: theme.palette.background.paper,
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      zIndex: theme.zIndex.drawer + 1,
    }}>
      <Toolbar sx={{ gap: 1, minHeight: '52px !important' }}>
        <IconButton onClick={onToggleSidebar} size="small" edge="start" sx={{ color: 'text.secondary' }}>
          <MenuIcon fontSize="small" />
        </IconButton>

        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 1 }}>
          <TreeIcon sx={{ color: 'primary.main', fontSize: 22 }} />
          {!isMobile && (
            <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.3px', color: 'text.primary', whiteSpace: 'nowrap' }}>
              Arbre Familial
            </Typography>
          )}
          {memberCount > 0 && (
            <Chip
              label={`${memberCount}`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 600, height: 18, fontSize: '0.68rem' }}
            />
          )}
        </Box>

        {/* View switcher */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          bgcolor: alpha(theme.palette.text.primary, 0.06),
          borderRadius: 2.5, p: 0.5,
        }}>
          {VIEW_BUTTONS.map(btn => {
            const active = view === btn.value;
            return (
              <Box
                key={btn.value}
                component="button"
                onClick={() => onSetView(btn.value)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  px: { xs: 1, sm: 1.5 }, py: 0.65,
                  borderRadius: 2, border: 'none', cursor: 'pointer',
                  bgcolor: active ? 'background.paper' : 'transparent',
                  color: active ? 'primary.main' : 'text.secondary',
                  boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                  fontFamily: 'inherit',
                  fontWeight: active ? 700 : 500,
                  fontSize: { xs: '0.72rem', sm: '0.78rem' },
                  transition: 'all 0.15s ease',
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    bgcolor: active ? 'background.paper' : alpha(theme.palette.text.primary, 0.05),
                    color: active ? 'primary.main' : 'text.primary',
                  },
                }}
              >
                {btn.icon}
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                  {btn.label}
                </Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                  {btn.labelShort}
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box sx={{ flex: 1 }} />

        {/* Import GEDCOM */}
        <input
          type="file"
          accept=".ged,.gedcom"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Tooltip title="Importer GEDCOM (.ged)" placement="bottom">
          <IconButton size="small" onClick={() => fileInputRef.current?.click()} sx={{ color: 'text.secondary' }}>
            <UploadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Export GEDCOM */}
        <Tooltip title="Exporter GEDCOM (.ged)" placement="bottom">
          <IconButton size="small" onClick={onExportGEDCOM} sx={{ color: 'text.secondary' }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Export image / PDF de l'arbre (variantes) */}
        <Tooltip title="Exporter l'arbre (image / PDF)" placement="bottom">
          <IconButton size="small" onClick={onOpenExport} sx={{ color: 'text.secondary' }}>
            <SaveAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {/* Comparateur de moteurs de rendu (Topola / family-chart / Balkan) */}
        <Tooltip title="Comparer les moteurs d'arbre" placement="bottom">
          <IconButton size="small" onClick={onOpenEngineCompare} sx={{ color: 'text.secondary' }}>
            <CompareIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1 }} />

        <IconButton onClick={toggleColorMode} size="small" sx={{ color: 'text.secondary' }}>
          {theme.palette.mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
        </IconButton>

        <Button
          variant="contained"
          startIcon={!isMobile ? <AddIcon fontSize="small" /> : undefined}
          onClick={onAddMember} size="small" disableElevation
          sx={{ borderRadius: 2, px: isMobile ? 1.5 : 2, minWidth: 'auto' }}
        >
          {isMobile ? <AddIcon fontSize="small" /> : 'Ajouter'}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export default AppHeader;
